/**
 * Theme presets, background patterns, and helpers used by both the
 * survey editor (preview) and the public survey page (render).
 *
 * A theme is a plain object — easy to JSON-serialize and stash in Mongo:
 *   {
 *     preset:           'sage' | 'sunset' | 'ocean' | 'plum' | 'mono' | 'cocoa' | 'custom',
 *     primary_color:    '#hex',
 *     secondary_color:  '#hex',
 *     background_color: '#hex',
 *     text_color:       '#hex',
 *     font_family:      'outfit' | 'inter' | 'playfair' | 'space-grotesk',
 *     background_style: 'solid' | 'gradient' | 'dots' | 'grid' | 'waves' | 'blobs',
 *     card_style:       'flat' | 'elevated' | 'glass' | 'bordered',
 *     logo_url:         '/uploads/...'
 *   }
 *
 * Older surveys may not have all fields — `withDefaults()` fills in safe values.
 */

export const THEME_PRESETS = [
  {
    id: 'sage',
    name: 'Calm Sage',
    description: 'Restful, healthcare-friendly',
    theme: {
      preset: 'sage',
      primary_color: '#7A8B7A',
      secondary_color: '#C67D63',
      background_color: '#FDFBF7',
      text_color: '#2C302E',
      font_family: 'outfit',
      background_style: 'soft-blobs',
      card_style: 'elevated',
    },
  },
  {
    id: 'ocean',
    name: 'Ocean Mist',
    description: 'Cool, professional, easy on the eyes',
    theme: {
      preset: 'ocean',
      primary_color: '#2E6F8E',
      secondary_color: '#5BA3C9',
      background_color: '#F2F7FA',
      text_color: '#0F2C3D',
      font_family: 'inter',
      background_style: 'waves',
      card_style: 'elevated',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset Warmth',
    description: 'Inviting, energetic, optimistic',
    theme: {
      preset: 'sunset',
      primary_color: '#D97757',
      secondary_color: '#F2B96D',
      background_color: '#FFF8F1',
      text_color: '#3A1E15',
      font_family: 'playfair',
      background_style: 'gradient',
      card_style: 'glass',
    },
  },
  {
    id: 'plum',
    name: 'Quiet Plum',
    description: 'Refined and contemplative',
    theme: {
      preset: 'plum',
      primary_color: '#6B4B7A',
      secondary_color: '#C9A0DC',
      background_color: '#F8F5FA',
      text_color: '#1F1525',
      font_family: 'playfair',
      background_style: 'dots',
      card_style: 'elevated',
    },
  },
  {
    id: 'mono',
    name: 'Modern Mono',
    description: 'Crisp, minimal, focus on content',
    theme: {
      preset: 'mono',
      primary_color: '#1F2937',
      secondary_color: '#6B7280',
      background_color: '#FFFFFF',
      text_color: '#0F172A',
      font_family: 'space-grotesk',
      background_style: 'grid',
      card_style: 'bordered',
    },
  },
  {
    id: 'cocoa',
    name: 'Warm Cocoa',
    description: 'Earthy, grounded, premium feel',
    theme: {
      preset: 'cocoa',
      primary_color: '#7B4B2A',
      secondary_color: '#D4A574',
      background_color: '#FAF6F0',
      text_color: '#2A1810',
      font_family: 'outfit',
      background_style: 'soft-blobs',
      card_style: 'glass',
    },
  },
  {
    id: 'tmc',
    name: 'TMC Brand',
    description: 'Navy text on cream, orange buttons',
    theme: {
      preset: 'tmc',
      // Buttons & action accents stay brand orange — high attention, on-brand
      primary_color: '#F99F1C',
      // Brand teal as the highlight color (links, icons, focus rings)
      secondary_color: '#2EC3E7',
      // Soft warm white with a hint of navy — much easier on the eyes than pure white
      background_color: '#F4F6FA',
      // Text is brand navy — passes WCAG AAA on this background
      text_color: '#003A60',
      font_family: 'montserrat',
      // Subtle gradient (navy-tinted cream) instead of busy patterns
      background_style: 'gradient',
      // Clean elevated cards keep the read flow uncluttered
      card_style: 'elevated',
      // No preset logo — the dark-on-light TMC mark isn't bundled.
      // Upload your standard navy-on-white logo via the Logo control above.
      logo_url: '',
    },
  },
];

export const FONT_OPTIONS = [
  { id: 'outfit', label: 'Outfit', stack: '"Outfit", system-ui, sans-serif', sample: 'Aa Bb Cc 123' },
  { id: 'inter', label: 'Inter', stack: '"Inter", system-ui, sans-serif', sample: 'Aa Bb Cc 123' },
  { id: 'playfair', label: 'Playfair Display', stack: '"Playfair Display", Georgia, serif', sample: 'Aa Bb Cc 123' },
  { id: 'space-grotesk', label: 'Space Grotesk', stack: '"Space Grotesk", system-ui, sans-serif', sample: 'Aa Bb Cc 123' },
  { id: 'montserrat', label: 'Montserrat', stack: '"Montserrat", system-ui, sans-serif', sample: 'Aa Bb Cc 123' },
];

export const BACKGROUND_STYLES = [
  { id: 'solid', label: 'Solid', hint: 'Just the background color' },
  { id: 'gradient', label: 'Soft Gradient', hint: 'Diagonal fade to secondary' },
  { id: 'dots', label: 'Dot Grid', hint: 'Subtle dotted pattern' },
  { id: 'grid', label: 'Line Grid', hint: 'Architectural grid' },
  { id: 'waves', label: 'Waves', hint: 'Gentle curved waves' },
  { id: 'soft-blobs', label: 'Soft Blobs', hint: 'Organic colored shapes' },
  { id: 'image', label: 'Custom Image', hint: 'Upload your own background' },
];

export const CARD_STYLES = [
  { id: 'flat', label: 'Flat', hint: 'No card — content sits on background' },
  { id: 'elevated', label: 'Elevated', hint: 'White card with soft shadow' },
  { id: 'glass', label: 'Glass', hint: 'Frosted translucent panel' },
  { id: 'bordered', label: 'Bordered', hint: 'Sharp 1px border, no shadow' },
];

const DEFAULTS = {
  preset: 'sage',
  primary_color: '#7A8B7A',
  secondary_color: '#C67D63',
  background_color: '#FDFBF7',
  text_color: '#2C302E',
  font_family: 'outfit',
  background_style: 'soft-blobs',
  card_style: 'elevated',
  logo_url: '',
  background_image: '',          // optional custom background image URL
  background_overlay: 0.35,      // 0..1 — dark scrim over the image for legibility
};

/** Fill in any fields the survey is missing so older surveys still render. */
export function withDefaults(theme = {}) {
  return { ...DEFAULTS, ...theme };
}

/** Get the font stack for a given font id. */
export function fontStack(id) {
  return FONT_OPTIONS.find((f) => f.id === id)?.stack || FONT_OPTIONS[0].stack;
}

/** Hex (#RRGGBB) -> rgba string. Falls back to the input if it can't parse. */
export function hexToRgba(hex, alpha = 1) {
  if (!hex || typeof hex !== 'string') return hex;
  const cleaned = hex.replace('#', '');
  if (cleaned.length !== 6) return hex;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Return inline CSS for the page-level background, given a theme.
 * Patterns are pure CSS (no image fetches) so they render instantly.
 */
export function backgroundCss(theme) {
  const t = withDefaults(theme);
  const bg = t.background_color;
  const primary = hexToRgba(t.primary_color, 0.07);
  const secondary = hexToRgba(t.secondary_color, 0.09);

  switch (t.background_style) {
    case 'image': {
      // Use the uploaded image. If none is set, gracefully fall back to solid.
      if (!t.background_image) return { backgroundColor: bg };
      const overlay = Math.max(0, Math.min(1, t.background_overlay ?? 0.35));
      // A dark scrim over the image keeps text legible on busy photos.
      const scrim = `linear-gradient(rgba(0,0,0,${overlay}), rgba(0,0,0,${overlay}))`;
      return {
        backgroundColor: bg,
        backgroundImage: `${scrim}, url("${t.background_image}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      };
    }
    case 'gradient':
      return {
        background: `linear-gradient(135deg, ${bg} 0%, ${hexToRgba(t.secondary_color, 0.18)} 100%)`,
      };
    case 'dots':
      return {
        backgroundColor: bg,
        backgroundImage: `radial-gradient(${primary} 1px, transparent 1px)`,
        backgroundSize: '20px 20px',
      };
    case 'grid':
      return {
        backgroundColor: bg,
        backgroundImage:
          `linear-gradient(${primary} 1px, transparent 1px), ` +
          `linear-gradient(90deg, ${primary} 1px, transparent 1px)`,
        backgroundSize: '32px 32px',
      };
    case 'waves':
      // SVG wave repeated horizontally — single inlined data URI, no network call.
      return {
        backgroundColor: bg,
        backgroundImage:
          `url("data:image/svg+xml;utf8,` +
          `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='40' viewBox='0 0 120 40'>` +
          `<path d='M0 20 Q 30 0 60 20 T 120 20' fill='none' stroke='${encodeURIComponent(primary)}' stroke-width='2'/>` +
          `</svg>")`,
        backgroundRepeat: 'repeat',
      };
    case 'soft-blobs':
      return {
        backgroundColor: bg,
        backgroundImage:
          `radial-gradient(at 12% 18%, ${secondary} 0px, transparent 45%),` +
          `radial-gradient(at 88% 10%, ${primary} 0px, transparent 45%),` +
          `radial-gradient(at 70% 92%, ${secondary} 0px, transparent 50%)`,
      };
    case 'solid':
    default:
      return { backgroundColor: bg };
  }
}

/** Inline CSS for the answer/question "card" element. */
export function cardCss(theme) {
  const t = withDefaults(theme);
  switch (t.card_style) {
    case 'flat':
      return { background: 'transparent', boxShadow: 'none', border: 'none' };
    case 'glass':
      return {
        background: hexToRgba('#FFFFFF', 0.55),
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: `1px solid ${hexToRgba('#FFFFFF', 0.6)}`,
        boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
      };
    case 'bordered':
      return {
        background: '#FFFFFF',
        border: `1px solid ${hexToRgba(t.text_color, 0.15)}`,
        boxShadow: 'none',
      };
    case 'elevated':
    default:
      return {
        background: '#FFFFFF',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        border: `1px solid ${hexToRgba(t.text_color, 0.06)}`,
      };
  }
}
